import { VectorHandling, ContentAnalysis, TagHandling } from "services";
import {
  enhancedCategoryKeywords,
  ContentWithMetadata,
  CategoryVector,
  ContentScore,
} from "types";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

const useContentOperations = <T extends ContentWithMetadata>(
  allowedGroups?: string[],
  type: "course" | "blog" = "course"
) => {
  const tagHandling = new TagHandling(allowedGroups);
  const vectorHandling = new VectorHandling();
  const contentAnalysis = new ContentAnalysis(
    tagHandling,
    enhancedCategoryKeywords
  );

  const getRelatedContent = (
    currentContent: T,
    allContent: T[],
    maxItems: number = 3,
    userPreferenceVector?: CategoryVector
  ): T[] => {
    if (!currentContent || !allContent || allContent.length === 0) {
      return [];
    }

    try {
      const otherContent = allContent.filter(
        (item) => item.id !== currentContent.id
      );
      const scoredContent = otherContent.map((content) => {
        const baseScore = contentAnalysis.getContentSimilarityScore(
          content,
          currentContent
        ) as ContentScore<T>;
        if (!userPreferenceVector) return baseScore;

        const contentVector = vectorHandling.createContentVector(
          content.tags || []
        );
        return {
          ...baseScore,
          score: baseScore.score + (contentVector ? 1 : 0),
        };
      });

      return scoredContent
        .sort((a, b) => b.score - a.score)
        .slice(0, maxItems)
        .map((score) => score.content);
    } catch (error) {
      console.error("Error in getRelatedContent:", error);
      return [];
    }
  };

  const getRecommendedContent = async (
    query: string = "",
    userVector: CategoryVector
  ): Promise<T[]> => {
    if (!userVector) return [];

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    try {
      const categoryVector = Array.isArray(userVector)
        ? vectorHandling.convertFlatVectorToCategoryVector(userVector)
        : userVector;

      const cleanedVector = Object.fromEntries(
        Object.entries(categoryVector).filter(([key]) => key !== "__typename")
      );

      const response: any = await client.queries.recommendCourses(
        {
          query: query ? query : "",
          userVector: cleanedVector,
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );

      if ("data" in response) {
        return response.data.items;
      }

      return [];
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      return [];
    }
  };

  return {
    getRelatedContent,
    getRecommendedContent,
  };
};

export default useContentOperations;
